<template>
    <v-form
        v-if="company"
        ref="form"
        v-model="valid"
        class="company-profile"
        data-cy="company-profile"
        :disabled="dontAllowEdit"
    >
        <v-row class="company-profile__header" dense>
            <v-col cols="12" sm="6" md="2" class="company-profile__header-left">
                <LocaleSwitcher @change="changeActiveLangCode" />
            </v-col>
            <v-col cols="12" sm="6" md="10" class="company-profile__header-right align-center">
                <p
                    v-if="company.id"
                    class="company-profile__header-right-txt width-max-content ml-auto pr-2 text-subtitle-2 text-grey font-weight-regular"
                >
                    {{ $t('id') }}: {{ company.id }}
                </p>
                <v-row v-if="!isMyCompanyPage" dense>
                    <v-switch
                        v-model="company.active"
                        color="primary"
                        class="ml-auto mr-4 mb-4"
                        :label="company.active ? $t('active') : $t('inactive')"
                        data-cy="company-status"
                        hide-details
                        density="compact"
                    />
                </v-row>
            </v-col>
        </v-row>
        <v-row class="company-profile__body">
            <v-col class="company-profile__body-main" cols="12" md="9">
                <v-row>
                    <v-col cols="12" md="3" class="col-title text-h6 text--secondary">
                        {{ $t('general_info') }}
                    </v-col>
                    <v-col cols="12" md="9">
                        <v-row dense>
                            <v-col cols="12" md="4">
                                <v-text-field
                                    v-model="company.name[activeLangCode]"
                                    :disabled="dontAllowEdit"
                                    variant="outlined"
                                    density="compact"
                                    color="primary"
                                    data-cy="company-name"
                                    :label="`${$t('company_name')}*`"
                                    :rules="[requiredRule('company_name')]"
                                >
                                    <template v-slot:append-inner>
                                        <span
                                            v-if="activeLangCode"
                                            class="fi"
                                            :class="[`fi-${useFlagIconCode(activeLangCode)}`]"
                                        />
                                    </template>
                                </v-text-field>
                            </v-col>
                            <v-col cols="12" md="4">
                                <v-autocomplete
                                    v-model="company.type"
                                    variant="outlined"
                                    density="compact"
                                    color="primary"
                                    data-cy="company-type"
                                    :items="mapedTypes"
                                    item-title="name"
                                    item-value="value"
                                    :label="`${$t('company_type')}*`"
                                    :disabled="!!company.id || disableType || dontAllowEdit"
                                    :rules="[requiredRule('company_type')]"
                                />
                            </v-col>
                            <v-col cols="12" md="2">
                                <v-text-field
                                    v-model="company.alias"
                                    :disabled="dontAllowEdit"
                                    variant="outlined"
                                    density="compact"
                                    color="primary"
                                    data-cy="alias"
                                    :label="$t('company_alias')"
                                />
                            </v-col>
                            <v-col cols="12" md="2">
                                <v-text-field
                                    v-model="company.companyCode"
                                    readonly
                                    variant="outlined"
                                    density="compact"
                                    color="primary"
                                    data-cy="company-code"
                                    :label="$t('company_code')"
                                    disabled
                                />
                            </v-col>
                        </v-row>
                        <v-row dense>
                            <v-col cols="12" md="6">
                                <v-autocomplete
                                    v-model="company.countryId"
                                    :disabled="dontAllowEdit"
                                    variant="outlined"
                                    density="compact"
                                    color="primary"
                                    data-cy="country-id"
                                    :items="countries"
                                    item-title="name"
                                    item-value="id"
                                    clearable
                                    :label="countryLabel"
                                    :rules="company?.type === 'INTERNAL_SUPPLIER' ? [] : [requiredRule('country')]"
                                    @update:model-value="loadCities"
                                />
                            </v-col>
                            <v-col cols="12" md="6">
                                <v-autocomplete
                                    v-model="company.cityId"
                                    :disabled="dontAllowEdit || isCitiesLoading"
                                    :items="cities"
                                    item-title="name"
                                    item-value="id"
                                    variant="outlined"
                                    density="compact"
                                    color="primary"
                                    data-cy="city-id"
                                    clearable
                                    :label="`${$t('city')}`"
                                    :loading="isCitiesLoading"
                                >
                                    <template v-slot:item="{item, props}">
                                        <v-list-item v-bind="props">
                                            <template v-slot:title>
                                                <v-lazy>{{ item.title }}</v-lazy>
                                            </template>
                                        </v-list-item>
                                    </template>
                                </v-autocomplete>
                            </v-col>
                        </v-row>
                        <v-row dense>
                            <v-col cols="12" md="3">
                                <v-text-field
                                    v-model="company.region[activeLangCode]"
                                    :disabled="dontAllowEdit"
                                    variant="outlined"
                                    density="compact"
                                    color="primary"
                                    data-cy="region"
                                    :label="`${$t('region')}`"
                                >
                                    <template v-slot:append-inner>
                                        <span
                                            v-if="activeLangCode"
                                            class="fi"
                                            :class="[`fi-${useFlagIconCode(activeLangCode)}`]"
                                        />
                                    </template>
                                </v-text-field>
                            </v-col>
                            <v-col cols="12" md="9">
                                <v-text-field
                                    v-model="company.address[activeLangCode]"
                                    :disabled="dontAllowEdit"
                                    variant="outlined"
                                    density="compact"
                                    color="primary"
                                    data-cy="address"
                                    :label="`${$t('address')}`"
                                >
                                    <template v-slot:append-inner>
                                        <span
                                            v-if="activeLangCode"
                                            class="fi"
                                            :class="[`fi-${useFlagIconCode(activeLangCode)}`]"
                                        />
                                    </template>
                                </v-text-field>
                            </v-col>
                        </v-row>
                        <v-row dense>
                            <v-col cols="12" md="3">
                                <v-text-field
                                    v-model="company.postalCode"
                                    :disabled="dontAllowEdit"
                                    variant="outlined"
                                    density="compact"
                                    color="primary"
                                    data-cy="postal-code"
                                    :label="`${$t('postal_code')}`"
                                />
                            </v-col>
                            <v-col cols="12" md="9">
                                <v-text-field
                                    v-model="company.postAddress[activeLangCode]"
                                    :disabled="dontAllowEdit"
                                    variant="outlined"
                                    density="compact"
                                    color="primary"
                                    data-cy="post-address"
                                    :label="`${$t('postal_address')}`"
                                >
                                    <template v-slot:append-inner>
                                        <span
                                            v-if="activeLangCode"
                                            class="fi"
                                            :class="[`fi-${useFlagIconCode(activeLangCode)}`]"
                                        />
                                    </template>
                                </v-text-field>
                            </v-col>
                        </v-row>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="12" md="3" class="col-title text-h6 text--secondary">
                        {{ $t('contacts') }}
                        <div class="col-subtitle px-0 font-italic text-subtitle-2">
                            {{ $t('main_contact_person') }}
                        </div>
                    </v-col>
                    <v-col cols="12" md="9">
                        <v-row dense>
                            <v-col cols="12" md="2">
                                <v-select
                                    v-model="company.contactPerson.prefix"
                                    :disabled="dontAllowEdit"
                                    variant="outlined"
                                    density="compact"
                                    color="primary"
                                    data-cy="contact-person-prefix"
                                    :items="['Mr', 'Ms', 'Mrs']"
                                    :label="$t('salutation')"
                                />
                            </v-col>
                            <v-col cols="12" md="5">
                                <v-text-field
                                    v-model="company.contactPerson.firstName[activeLangCode]"
                                    :disabled="dontAllowEdit"
                                    variant="outlined"
                                    density="compact"
                                    color="primary"
                                    data-cy="contact-person-first-name"
                                    :label="$t('first_name')"
                                >
                                    <template v-slot:append-inner>
                                        <span
                                            v-if="activeLangCode"
                                            class="fi"
                                            :class="[`fi-${useFlagIconCode(activeLangCode)}`]"
                                        />
                                    </template>
                                </v-text-field>
                            </v-col>
                            <v-col cols="12" md="5">
                                <v-text-field
                                    v-model="company.contactPerson.lastName[activeLangCode]"
                                    :disabled="dontAllowEdit"
                                    variant="outlined"
                                    density="compact"
                                    color="primary"
                                    data-cy="contact-person-last-name"
                                    :label="$t('last_name')"
                                >
                                    <template v-slot:append-inner>
                                        <span
                                            v-if="activeLangCode"
                                            class="fi"
                                            :class="[`fi-${useFlagIconCode(activeLangCode)}`]"
                                        />
                                    </template>
                                </v-text-field>
                            </v-col>
                        </v-row>
                        <v-row dense>
                            <v-col cols="12" md="4">
                                <phone-field
                                    v-model="company.phones[0]"
                                    :label="`${$t('phone')} 1`"
                                    density="compact"
                                    color="primary"
                                    data-cy="phone-1"
                                    :disabled="dontAllowEdit"
                                />
                            </v-col>
                            <v-col cols="12" md="4">
                                <phone-field
                                    v-model="company.phones[1]"
                                    :label="`${$t('phone')} 2`"
                                    density="compact"
                                    color="primary"
                                    data-cy="phone-2"
                                    :disabled="dontAllowEdit"
                                />
                            </v-col>
                            <v-col cols="12" md="4">
                                <phone-field
                                    v-model="company.phones[2]"
                                    :label="`${$t('phone')} 3`"
                                    density="compact"
                                    color="primary"
                                    data-cy="phone-3"
                                    :disabled="dontAllowEdit"
                                />
                            </v-col>
                        </v-row>
                        <v-row dense>
                            <v-col cols="12" md="4">
                                <v-text-field
                                    v-model="company.emails[0]"
                                    :disabled="dontAllowEdit"
                                    variant="outlined"
                                    density="compact"
                                    color="primary"
                                    data-cy="email-1"
                                    :label="`${$t('email')} 1`"
                                    :rules="[emailRule('email')]"
                                />
                            </v-col>
                            <v-col cols="12" md="4">
                                <v-text-field
                                    v-model="company.emails[1]"
                                    :disabled="dontAllowEdit"
                                    variant="outlined"
                                    density="compact"
                                    color="primary"
                                    data-cy="email-2"
                                    :label="`${$t('email')} 2`"
                                    :rules="[emailRule('email')]"
                                />
                            </v-col>
                            <v-col cols="12" md="4">
                                <v-text-field
                                    v-model="company.emails[2]"
                                    :disabled="dontAllowEdit"
                                    variant="outlined"
                                    density="compact"
                                    color="primary"
                                    data-cy="email-3"
                                    :label="`${$t('email')} 3`"
                                    :rules="[emailRule('email')]"
                                />
                            </v-col>
                        </v-row>
                        <v-row dense>
                            <v-col cols="12" md="4">
                                <v-text-field
                                    v-model="company.fax"
                                    :disabled="dontAllowEdit"
                                    variant="outlined"
                                    density="compact"
                                    color="primary"
                                    data-cy="fax"
                                    :label="$t('fax')"
                                />
                            </v-col>
                            <v-col cols="12" md="8">
                                <v-text-field
                                    v-model="company.url"
                                    :disabled="dontAllowEdit"
                                    variant="outlined"
                                    density="compact"
                                    color="primary"
                                    data-cy="url"
                                    :label="$t('url')"
                                />
                            </v-col>
                        </v-row>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="12" md="3" class="col-title text-h6 text--secondary">
                        {{ $t('additional_info') }}
                    </v-col>
                    <v-col cols="12" md="9">
                        <v-row dense>
                            <v-col cols="12" md="8">
                                <v-text-field
                                    v-model="company.officialCompanyName[activeLangCode]"
                                    :disabled="dontAllowEdit"
                                    variant="outlined"
                                    density="compact"
                                    color="primary"
                                    data-cy="official-company-name"
                                    :label="`${$t('company_official_name')}`"
                                />
                            </v-col>
                            <v-col cols="6" md="2">
                                <v-text-field
                                    v-model="company.externalCode"
                                    :disabled="dontAllowEdit"
                                    variant="outlined"
                                    density="compact"
                                    color="primary"
                                    data-cy="external-code"
                                    :label="$t('external_code')"
                                />
                            </v-col>
                            <v-col cols="6" md="2">
                                <v-text-field
                                    v-model="company.taxRegistrationReason"
                                    :disabled="dontAllowEdit"
                                    variant="outlined"
                                    density="compact"
                                    color="primary"
                                    data-cy="company-kpp"
                                    :label="$t('company_kpp')"
                                />
                            </v-col>
                        </v-row>
                        <v-row dense>
                            <v-col cols="6" md="4">
                                <v-text-field
                                    v-model="company.vatNumber"
                                    :disabled="dontAllowEdit"
                                    variant="outlined"
                                    density="compact"
                                    color="primary"
                                    data-cy="vat-number"
                                    :label="`${$t('vat_number')}`"
                                />
                            </v-col>
                            <v-col cols="6" md="4">
                                <v-text-field
                                    v-model="company.iataNumber"
                                    :disabled="dontAllowEdit"
                                    variant="outlined"
                                    density="compact"
                                    color="primary"
                                    data-cy="iata-number"
                                    :label="$t('iata_number')"
                                />
                            </v-col>
                            <v-col md="4">
                                <v-text-field
                                    v-model="company.docexNumber"
                                    :disabled="dontAllowEdit"
                                    variant="outlined"
                                    density="compact"
                                    color="primary"
                                    data-cy="docex_number"
                                    :label="$t('docex_number')"
                                />
                            </v-col>
                        </v-row>
                        <v-row v-if="isShowConsortiumns" dense>
                            <v-col md="4">
                                <v-select
                                    v-model="company.consortium"
                                    density="compact"
                                    :items="consortiumns"
                                    clearable
                                    hide-details
                                    color="primary"
                                    data-cy="consortium"
                                    :label="$t('consortium')"
                                />
                            </v-col>
                            <v-col md="4">
                                <v-select
                                    v-model="company.subconsortium"
                                    density="compact"
                                    :items="subConsortiums"
                                    clearable
                                    hide-details
                                    color="primary"
                                    data-cy="subconsortium"
                                    :label="$t('sub_consortium')"
                                />
                            </v-col>
                        </v-row>
                    </v-col>
                </v-row>
                <v-row v-if="isTO1">
                    <v-col cols="12" md="3" class="col-title text-h6 text--secondary">
                        {{ $t('internal_comments') }}
                        <div class="col-subtitle px-0 font-italic text-subtitle-2">
                            {{ $t('visible_for_to_only') }}
                        </div>
                    </v-col>
                    <v-col cols="12" md="9">
                        <v-textarea
                            v-model="company.comments[activeLangCode]"
                            :disabled="dontAllowEdit"
                            variant="outlined"
                            color="primary"
                            data-cy="comments"
                        >
                            <template v-slot:append-inner>
                                <span
                                    v-if="activeLangCode"
                                    class="fi"
                                    :class="[`fi-${useFlagIconCode(activeLangCode)}`]"
                                />
                            </template>
                        </v-textarea>
                    </v-col>
                </v-row>
            </v-col>
            <v-col cols="12" md="3" class="company-profile__body-sidebar pl-2 order-first order-md-last">
                <v-select
                    v-model="company.language"
                    :items="locales"
                    :label="$t('language')"
                    clearable
                    density="compact"
                    variant="underlined"
                    color="primary"
                    data-cy="company-language"
                />
                <div class="relative-position px-2 pb-10">
                    <v-img
                        :src="logo ? logo : $filters.imageUrl(company.logo, 650)"
                        :lazy-src="$filters.srcHost('/placeholder.png')"
                        :max-height="160"
                        class="mb-4"
                    />
                    <v-row>
                        <div class="ml-auto width-max-content">
                            <v-file-input
                                v-model="logoArray"
                                prepend-icon="mdi-pencil"
                                small-chips
                                variant="outlined"
                                density="compact"
                                accept="image/jpg, image/jpeg, image/png"
                                hide-details
                                :label="$t('select_photo')"
                                data-cy="add-logo"
                                class="hide-input"
                                @change="loadFile(logoArray, 'logo')"
                            />
                        </div>
                        <v-icon
                            icon="mdi-close"
                            color="error"
                            data-cy="remove-logo"
                            class="cursor-pointer ml-2"
                            @click="removeFile('logo')"
                        />
                    </v-row>
                </div>
                <div v-if="isMyCompanyPage">
                    <h4 class="text-h6 text--secondary">
                        {{ $t('signature') }}
                    </h4>
                    <div class="relative-position px-2 pb-10">
                        <v-img
                            v-if="company.id"
                            :src="signature ? signature : $filters.imageUrl(company.signature, 650)"
                            :lazy-src="$filters.srcHost('/placeholder.png')"
                            :max-height="160"
                            class="mb-4"
                        />
                        <v-row>
                            <div class="ml-auto width-max-content">
                                <v-file-input
                                    v-model="filesArray"
                                    prepend-icon="mdi-pencil"
                                    small-chips
                                    variant="outlined"
                                    density="compact"
                                    accept="image/jpg, image/jpeg, image/png"
                                    hide-details
                                    :label="$t('select_photo')"
                                    data-cy="avatar"
                                    class="hide-input"
                                    @change="loadFile(filesArray, 'signature')"
                                />
                            </div>
                            <v-icon
                                icon="mdi-close"
                                color="error"
                                data-cy="remove-avatar"
                                class="cursor-pointer ml-2"
                                @click="removeFile('signature')"
                            />
                        </v-row>
                    </div>
                </div>
                <v-btn v-if="false" block>
                    {{ $t('download') }}
                </v-btn>
                <v-select
                    v-if="isCRMExtendedModule && !isAgency && !isTO2 && !isMyCompanyPage"
                    v-model="company.stage"
                    :items="useStages"
                    :label="$t('stage')"
                    clearable
                    density="compact"
                    variant="underlined"
                    color="primary"
                    data-cy="stage"
                />
                <v-select
                    v-if="isCRMExtendedModule && !isAgency && !isTO2 && !isMyCompanyPage"
                    v-model="company.priorityLevel"
                    :items="usePriorities"
                    :label="$t('priority')"
                    clearable
                    density="compact"
                    variant="underlined"
                    color="primary"
                    data-cy="priority"
                />
                <v-select
                    v-if="isCRMExtendedModule && !isAgency && !isTO2 && !isMyCompanyPage"
                    v-model="company.industry"
                    :items="useIndustries"
                    :label="$t('industry')"
                    clearable
                    density="compact"
                    variant="underlined"
                    color="primary"
                    data-cy="industry"
                />
                <v-select
                    v-if="isCRMExtendedModule && !isAgency && !isTO2 && !isMyCompanyPage"
                    v-model="company.companySize"
                    :items="useCompanySize"
                    :label="$t('company_size')"
                    clearable
                    density="compact"
                    variant="underlined"
                    color="primary"
                    data-cy="company-size"
                />
                <v-text-field
                    v-model="company.externalId"
                    :label="$t('external_member_id')"
                    type="number"
                    hide-spin-buttons
                    density="compact"
                    variant="underlined"
                    color="primary"
                    data-cy="external-member-id"
                />
            </v-col>
        </v-row>
        <v-row no-gutters class="company-profile__footer gc-4">
            <v-btn
                :loading="isLoading"
                :disabled="isLoading || !valid || dontAllowEdit"
                class="btn-save"
                data-cy="save"
                @click="save"
            >
                {{ $t('save') }}
            </v-btn>
            <v-btn class="btn-cancel" data-cy="cancel" variant="text" @click="cancel">
                {{ $t('cancel') }}
            </v-btn>
        </v-row>
    </v-form>
</template>

<script lang="ts">
    import {Component, Emit, mixins, Prop} from 'vue-facing-decorator'
    import {FormValidationRulesMixin} from '@/mixins/FormValidationRulesMixin.vue'
    import PhoneField from '~/components/snippets/forms/PhoneField.vue'
    import type {VForm} from 'vuetify/components'
    import type {CompanyProfileType} from '~/utils/types/account/companies/types'
    import {blobToBase64, clone} from '~/utils/helpers'
    import {initCompanyProfile} from '~/utils/types/account/companies/helpers'
    import {Company} from '~/utils/types/types'

    @Component({
        components: {PhoneField},
    })
    export class CompanyProfile extends mixins(FormValidationRulesMixin) {
        @Prop({default: () => initCompanyProfile()}) companyData: CompanyProfileType
        @Prop({default: false, type: Boolean}) disableType: boolean
        @Prop({default: false, type: Boolean}) noRedirect: boolean

        types = ['INTERNAL_SUPPLIER', 'TOUR_OPERATOR', 'TOUR_AGENCY', 'CORPORATOR']
        isLoading: boolean = false
        valid: boolean = false
        cities = []
        isCitiesLoading = false
        company = initCompanyProfile()

        activeLangCode: string = 'en'
        signature: string = null
        filesArray = []
        logo: string = null
        logoArray = []

        consortiumns = []
        subConsortiums = []

        async mounted() {
            this.activeLangCode = this.company.language ? this.company.language : this.locale
            this.company = clone(this.companyData)
            if (this.company?.id) {
                this.types = ['INTERNAL_SUPPLIER', 'TOUR_OPERATOR_1LEVEL', 'TOUR_OPERATOR', 'TOUR_AGENCY', 'CORPORATOR']
                await this.form.validate()
                await this.loadCities(this.company.countryId)
            } else {
                if (this.isAgency) {
                    this.types = ['CORPORATOR']
                }
                if (this.isTO2) {
                    this.types = ['TOUR_AGENCY', 'CORPORATOR']
                }
            }
            if (this.isShowConsortiumns) {
                try {
                    this.consortiumns = (await this.$api.consortiums.get()).consortiums
                    this.subConsortiums = (await this.$api.subConsortiums.get()).subConsortiums
                    // eslint-disable-next-line no-empty
                } catch (e) {}
            }
        }

        get isShowConsortiumns() {
            return this.isTourOperator && (this.company.type === 'TOUR_AGENCY' || this.company.type === 'TOUR_OPERATOR')
        }

        get isTourOperator() {
            return authStore.isTourOperator
        }

        get isMyCompanyPage(): boolean {
            return authStore.person.companyId === this.company?.id
        }

        get locale(): string {
            return this.$i18n.locale
        }

        get form(): VForm {
            return this.$refs.form as VForm
        }

        @Emit()
        async save() {
            this.isLoading = true
            let company = null

            try {
                if (this.company.id) {
                    await this.$api.companies.put(
                        this.company.id,
                        this.mapToRequest(companyInfoMessageToUpdateCompanyRQ(this.company))
                    )
                } else {
                    company = await this.$api.companies.post(
                        this.mapToRequest(companyInfoMessageToCreateCompanyRQ(this.company))
                    )
                }
                this.$toast.success(this.$t('messages.success.company_saved'))
                if (!this.company.id && !this.noRedirect) {
                    this.$router.back()
                }
            } catch {
                this.$toast.error(this.$t('data_update_fail'))
            }
            this.isLoading = false

            return company
        }

        cancel(): void {
            const routeMap = {
                [Company.Corporator]: 'companyCorporateClients',
                [Company.HubSupplier]: 'companySuppliers',
                [Company.InternalSupplier]: 'companySuppliers',
            }

            if (this.company?.id === authStore.person?.companyId) {
                this.$router.push({name: 'home'})
            } else if (typeof this.company.type === 'string') {
                const routeName = routeMap[this.company.type] || 'companyAgencies'
                this.$router.push({name: routeName})
            } else {
                this.$router.back()
            }
        }

        mapToRequest(rq) {
            if (this.isMyCompanyPage) {
                rq.signature = this.signature ? this.signature.split(',')[1] : this.company.signature
            }
            rq.logo = this.logo ? this.logo.split(',')[1] : rq.logo
            return rq
        }

        changeActiveLangCode(code) {
            this.activeLangCode = code
        }

        get dontAllowEdit(): boolean {
            return false
        }

        get countries() {
            return runtimeStore.countries
        }

        get mapedTypes() {
            return this.types.map(type => {
                return {value: type, name: this.$t(`companyTypes.${type}`)}
            })
        }

        async loadCities(countryId): Promise<void> {
            try {
                this.isCitiesLoading = true
                const {cities} = await this.$api.locations.get({
                    limitCities: -1,
                    orderBy: 'ALPHABET',
                    countryId: countryId,
                    applyRestriction: false,
                })
                this.cities = cities
            } catch (e) {
                this.cities = []
            } finally {
                this.isCitiesLoading = false
            }
        }

        get isTO1(): boolean {
            return authStore.isTO1
        }

        get isAgency() {
            return authStore.isAgency
        }

        get isTO2() {
            return authStore.isTO2
        }

        get locales() {
            const availableLocales = Object.values(this.$conf.public.locales)
            return availableLocales.map(lang => {
                return {value: lang, title: this.$t(`languages.${lang}`), icon: useFlagIconCode.value(lang)}
            })
        }

        get isCRMExtendedModule() {
            return runtimeStore.CRMSettings?.extendedCrm || false
        }

        async loadFile(fileArray: File[], target: 'signature' | 'logo'): Promise<void> {
            if (fileArray) {
                this[target] = await blobToBase64(fileArray)
            } else {
                this[target] = null
            }
        }

        removeFile(target: 'signature' | 'logo'): void {
            this[target] = null
            this.company[target] = null
        }

        get countryLabel() {
            return this.company?.type === 'INTERNAL_SUPPLIER' ? this.$t('country') : `${this.$t('country')}*`
        }
    }

    export default CompanyProfile
</script>
